import { useEffect, useState, type ReactNode } from 'react';
import type { ElementRef } from '@authsemply/shared';
import { ChampRecherche } from '../../components/ChampRecherche';
import { referentiels, type ActiviteRef } from '../../lib/referentiels';

/**
 * Les champs d'identité d'un dossier qui se SAISISSENT PAR RECHERCHE dans les
 * référentiels du portail (14/10/2026) : code APE, forme juridique, pays, code
 * postal et ville. Une valeur absente des tables est refusée à
 * l'enregistrement : choisir dans la liste évite d'y arriver.
 */
interface Props {
  /** Valeur courante d'un champ (saisie en cours, sinon enregistrée). */
  valeur: (cle: string) => string;
  /** Change un ou plusieurs champs d'un coup (un choix en remplit plusieurs). */
  onChange: (champs: Record<string, string>) => void;
  erreurs?: Record<string, string>;
  disabled?: boolean;
  prefixe: string;
  /** Champs marqués obligatoires (astérisque). */
  requis?: readonly string[];
  /** Précision affichée sous le champ (code APE à venir…). */
  complement?: ReactNode;
}

const Etoile = ({ si }: { si: boolean }) => (si ? <span className="dos-requis" aria-hidden="true"> *</span> : null);

/**
 * Code APE : recherche par code ou par activité ; le libellé et la
 * NOMENCLATURE suivent le code (NAF rév. 2 et NAF 2025 coexistent, 14/10/2026).
 */
export function ChampActivite({ valeur, onChange, erreurs = {}, disabled, prefixe, requis = [], complement }: Props) {
  const libelle = valeur('apeLabel');
  return (
    <ChampRecherche<ActiviteRef>
      id={`${prefixe}-apeCode`}
      libelle={
        <>
          Code APE (activité)
          <Etoile si={requis.includes('apeCode')} />
        </>
      }
      valeur={valeur('apeCode')}
      placeholder="ex. 62.01Z ou « informatique »"
      maxLength={60}
      disabled={disabled}
      erreur={erreurs.apeCode}
      aide={
        <>
          {libelle || 'Tapez un code ou des mots de l’activité, puis choisissez dans la liste.'}
          {complement ? <span className="dos-aide-suite">{complement}</span> : null}
        </>
      }
      onSaisie={(t) => onChange({ apeCode: t, apeLabel: '', apeNomenclature: '' })}
      onChoix={(a) => onChange({ apeCode: a.code, apeLabel: a.libelle, apeNomenclature: a.nomenclature })}
      rechercher={async (t) =>
        (await referentiels.activites(t)).map((a) => ({
          cle: `${a.nomenclature}-${a.code}`,
          titre: a.code,
          detail: `${a.libelle} · ${a.nomenclatureLibelle ?? a.nomenclature}`,
          donnee: a,
        }))
      }
    />
  );
}

/** Forme juridique : code INSEE, abréviation (« SASU ») ou mots du libellé. */
export function ChampFormeJuridique({ valeur, onChange, erreurs = {}, disabled, prefixe }: Props) {
  return (
    <ChampRecherche<ElementRef>
      id={`${prefixe}-legalForm`}
      libelle="Forme juridique"
      valeur={valeur('legalForm')}
      placeholder="ex. SASU, SARL, entrepreneur individuel"
      maxLength={255}
      minimum={1}
      disabled={disabled}
      erreur={erreurs.legalForm}
      aide={valeur('legalFormCode') ? `Catégorie juridique INSEE ${valeur('legalFormCode')}` : undefined}
      onSaisie={(t) => onChange({ legalForm: t, legalFormCode: '' })}
      onChoix={(f) => onChange({ legalForm: f.libelle, legalFormCode: f.code })}
      rechercher={async (t) =>
        (await referentiels.formesJuridiques(t)).map((f) => ({ cle: f.code, titre: f.libelle, detail: f.code, donnee: f }))
      }
    />
  );
}

/** Pays : liste complète (≈ 210), la France en tête. */
export function ChampPays({ valeur, onChange, erreurs = {}, disabled, prefixe }: Props) {
  const [liste, setListe] = useState<ElementRef[]>([]);
  useEffect(() => {
    let actif = true;
    referentiels
      .pays()
      .then((p) => actif && setListe(p))
      .catch(() => actif && setListe([{ code: 'FR', libelle: 'France' }]));
    return () => {
      actif = false;
    };
  }, []);
  const courant = valeur('country') || 'FR';
  return (
    <div className={`field${erreurs.country ? ' has-erreur' : ''}`}>
      <label htmlFor={`${prefixe}-country`}>Pays</label>
      <select
        id={`${prefixe}-country`}
        className="ref-select"
        value={courant}
        disabled={disabled || liste.length === 0}
        onChange={(e) => onChange({ country: e.target.value })}
      >
        {liste.length === 0 ? <option value={courant}>{courant}</option> : null}
        {liste.map((p) => (
          <option key={p.code} value={p.code}>
            {p.libelle}
          </option>
        ))}
      </select>
      {erreurs.country ? <p className="dos-erreur">{erreurs.country}</p> : null}
    </div>
  );
}

/**
 * Code postal et ville : la recherche part de l'un ou de l'autre, et un choix
 * remplit les deux. Hors de France, saisie libre (la base La Poste ne couvre
 * que les adresses françaises).
 */
export function ChampsCommune({ valeur, onChange, erreurs = {}, disabled, prefixe, requis = [] }: Props) {
  const france = (valeur('country') || 'FR') === 'FR';
  if (!france) {
    return (
      <div className="ref-commune">
        {(['postCode', 'city'] as const).map((cle) => (
          <div key={cle} className={`field${erreurs[cle] ? ' has-erreur' : ''}`}>
            <label htmlFor={`${prefixe}-${cle}`}>
              {cle === 'postCode' ? 'Code postal' : 'Ville'}
              <Etoile si={requis.includes(cle)} />
            </label>
            <input
              id={`${prefixe}-${cle}`}
              value={valeur(cle)}
              disabled={disabled}
              maxLength={cle === 'postCode' ? 10 : 120}
              onChange={(e) => onChange({ [cle]: e.target.value })}
            />
            {erreurs[cle] ? <p className="dos-erreur">{erreurs[cle]}</p> : null}
          </div>
        ))}
      </div>
    );
  }
  const rechercher = async (t: string, proche = '') =>
    (await referentiels.communes(t, proche)).map((c) => ({
      cle: `${c.codePostal}-${c.nom}`,
      titre: c.codePostal,
      detail: c.nom,
      donnee: c,
    }));
  const choisir = (c: { codePostal: string; nom: string }) => onChange({ postCode: c.codePostal, city: c.nom });
  // Par la ville : les communes du code postal saisi d'abord, puis celles du
  // même département (« castelnau » avec 34170 → Castelnau-le-Lez en tête).
  const rechercherVille = (t: string) => rechercher(t, valeur('postCode'));
  return (
    <div className="ref-commune">
      <ChampRecherche
        id={`${prefixe}-postCode`}
        libelle={
          <>
            Code postal
            <Etoile si={requis.includes('postCode')} />
          </>
        }
        valeur={valeur('postCode')}
        inputMode="numeric"
        maxLength={5}
        disabled={disabled}
        erreur={erreurs.postCode}
        onSaisie={(t) => onChange({ postCode: t.replace(/\D/g, '') })}
        onChoix={choisir}
        rechercher={rechercher}
      />
      <ChampRecherche
        id={`${prefixe}-city`}
        libelle={
          <>
            Ville
            <Etoile si={requis.includes('city')} />
          </>
        }
        valeur={valeur('city')}
        maxLength={120}
        disabled={disabled}
        erreur={erreurs.city}
        onSaisie={(t) => onChange({ city: t })}
        onChoix={choisir}
        rechercher={rechercherVille}
      />
    </div>
  );
}
